/**
 * Fallback lab (localStorage) quando a API Ata / waybee-api não está no ar.
 * Contrato alinhado à API: embedding = fingerprint do cluster (evidência);
 * suggest por amostras, hint só como boost leve.
 */

const STORAGE_KEY = "ataInteligenteVoiceProfiles_v1";
const HINT_BOOST = 0.03;

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : { profiles: [], samples: [] };
    return {
      profiles: Array.isArray(parsed.profiles) ? parsed.profiles : [],
      samples: Array.isArray(parsed.samples) ? parsed.samples : []
    };
  } catch (e) {
    return { profiles: [], samples: [] };
  }
}

function writeAll(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/** Fingerprint lab estável do falante (nunca personKey). */
export function labVoiceFingerprint({ meetingId, speakerClusterId, voiceKey }) {
  const mid = String(meetingId || "");
  const vk = String(voiceKey || speakerClusterId || "")
    .trim()
    .toLowerCase();
  return `ac:${mid}|${vk}`;
}

function hashScore(a, b) {
  // Similaridade fake estável: mesmo fingerprint → ~0.96; diferente → baixo
  if (!a || !b) return 0.2;
  if (a === b) return 0.96;
  let h = 0;
  const s = `${a}::${b}`;
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(h, 31) + s.charCodeAt(i);
    h %= 2147483647;
  }
  return 0.15 + (Math.abs(h) % 40) / 100;
}

export function labRegisterVoiceSamples({ meetingId, clusters }) {
  const db = readAll();
  const mid = String(meetingId);
  let created = 0;
  (clusters || []).forEach(c => {
    const clusterId = c.speakerClusterId || c.id;
    if (!clusterId) return;
    db.samples = db.samples.filter(
      s => !(s.meetingId === mid && s.speakerClusterId === clusterId)
    );
    const n = Math.max(1, Math.min(12, Number(c.sampleCount) || 3));
    const fingerprint = labVoiceFingerprint({
      meetingId: mid,
      speakerClusterId: clusterId,
      voiceKey: c.voiceKey
    });
    for (let i = 0; i < n; i += 1) {
      db.samples.push({
        id: `lab_smp_${Date.now()}_${clusterId}_${i}`,
        meetingId: mid,
        speakerClusterId: clusterId,
        personKey: c.personKey || null,
        fingerprint,
        createdAt: new Date().toISOString()
      });
      created += 1;
    }
  });
  writeAll(db);
  return { created, modelId: "lab-local", samples: [], scoreSource: "sample_embedding" };
}

export function labConfirmSpeakerVoice({
  meetingId,
  speakerClusterId,
  personKey,
  displayName,
  voiceKey
}) {
  const db = readAll();
  const mid = String(meetingId);
  const clusterId = String(speakerClusterId || "");
  const key = String(personKey || "");
  if (!clusterId || !key) {
    throw new Error("speakerClusterId e personKey são obrigatórios");
  }

  const fingerprint = labVoiceFingerprint({
    meetingId: mid,
    speakerClusterId: clusterId,
    voiceKey
  });

  let samples = db.samples.filter(
    s =>
      s.meetingId === mid &&
      s.speakerClusterId === clusterId &&
      !s.excludedAt
  );
  if (samples.length < 3) {
    labRegisterVoiceSamples({
      meetingId: mid,
      clusters: [
        {
          speakerClusterId: clusterId,
          voiceKey,
          sampleCount: 3,
          personKey: key
        }
      ]
    });
    const next = readAll();
    db.profiles = next.profiles;
    db.samples = next.samples;
    samples = db.samples.filter(
      s =>
        s.meetingId === mid &&
        s.speakerClusterId === clusterId &&
        !s.excludedAt
    );
  }

  // Amarra person_key; preserva fingerprint da evidência
  db.samples = db.samples.map(s =>
    s.meetingId === mid && s.speakerClusterId === clusterId && !s.excludedAt
      ? { ...s, personKey: key, fingerprint: s.fingerprint || fingerprint }
      : s
  );

  const personSamples = db.samples.filter(
    s => s.personKey === key && !s.excludedAt
  );
  const existing = db.profiles.findIndex(p => p.personKey === key);
  const priorFp =
    existing >= 0 ? db.profiles[existing].fingerprint : null;
  const evidenceFp =
    (personSamples[0] && personSamples[0].fingerprint) || fingerprint;

  let matchConfidence = priorFp
    ? hashScore(fingerprint, priorFp)
    : hashScore(fingerprint, evidenceFp);
  matchConfidence = Math.max(matchConfidence, 0.85);

  const profile = {
    id: existing >= 0 ? db.profiles[existing].id : `lab_vp_${Date.now()}`,
    personKey: key,
    displayName: displayName || key,
    fingerprint: evidenceFp,
    sampleCount: Math.max(personSamples.length, 3),
    consentAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  if (existing >= 0) db.profiles[existing] = profile;
  else db.profiles.push(profile);

  writeAll(db);
  return {
    modelId: "lab-local",
    speakerClusterId: clusterId,
    personKey: key,
    sampleCount: profile.sampleCount,
    matchConfidence,
    scoreSource: "sample_embedding",
    reinforced: true,
    profile,
    labFallback: true
  };
}

export function labSuggestSpeakersByVoice({ meetingId, clusters }) {
  const db = readAll();
  const mid = String(meetingId || "");
  const results = (clusters || []).map(c => {
    const clusterId = c.speakerClusterId || c.id;
    const turnId = c.turnId || null;
    const hint = c.personKeyHint || null;
    const queryFp =
      (
        db.samples.find(
          s =>
            s.meetingId === mid &&
            s.speakerClusterId === clusterId &&
            !s.excludedAt &&
            s.fingerprint
        ) || {}
      ).fingerprint ||
      labVoiceFingerprint({
        meetingId: mid,
        speakerClusterId: clusterId,
        voiceKey: c.voiceKey
      });

    const matches = (db.profiles || [])
      .filter(p => p.consentAt || p.sampleCount > 0)
      .map(p => {
        let score = hashScore(queryFp, p.fingerprint || p.personKey);
        let hintBoosted = false;
        if (hint && p.personKey === hint) {
          score = Math.min(0.99, score + HINT_BOOST);
          hintBoosted = true;
        }
        return {
          personKey: p.personKey,
          displayName: p.displayName,
          sampleCount: p.sampleCount,
          score,
          scoreSource: "sample_embedding",
          hintBoosted
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    return {
      speakerClusterId: clusterId,
      turnId,
      scoreSource: "sample_embedding",
      matches
    };
  });
  return {
    modelId: "lab-local",
    results,
    labFallback: true,
    scoreSource: "sample_embedding"
  };
}

export function labExcludeVoiceSample({
  meetingId,
  sampleId,
  speakerClusterId,
  turnId
}) {
  const db = readAll();
  const mid = String(meetingId);
  const sid = sampleId || turnId || null;
  let excluded = 0;
  db.samples = db.samples.map(s => {
    if (s.meetingId !== mid) return s;
    if (s.excludedAt) return s;
    const hit =
      (sid && (s.id === sid || s.turnId === sid)) ||
      (speakerClusterId &&
        s.speakerClusterId === String(speakerClusterId) &&
        (!sid || s.id === sid || s.turnId === sid));
    if (!hit) return s;
    excluded += 1;
    return { ...s, excludedAt: new Date().toISOString() };
  });
  writeAll(db);
  return {
    modelId: "lab-local",
    excluded: excluded > 0,
    count: excluded,
    labFallback: true
  };
}

/** Soft-delete de todas as amostras da reunião no lab + atualiza sampleCount dos perfis. */
export function labPurgeMeetingVoice({ meetingId, reason = "meeting_deleted" }) {
  const db = readAll();
  const mid = String(meetingId);
  const now = new Date().toISOString();
  const affectedKeys = new Set();
  let samplesExcluded = 0;
  const sampleIds = [];

  db.samples = db.samples.map(s => {
    if (s.meetingId !== mid || s.excludedAt) return s;
    samplesExcluded += 1;
    sampleIds.push(s.id);
    if (s.personKey) affectedKeys.add(s.personKey);
    return {
      ...s,
      excludedAt: now,
      excludedReason: reason
    };
  });

  const profilesUpdated = [];
  db.profiles = (db.profiles || []).map(p => {
    if (!affectedKeys.has(p.personKey)) return p;
    const remaining = db.samples.filter(
      s => s.personKey === p.personKey && !s.excludedAt
    );
    const sampleCount = remaining.length;
    const fingerprint =
      (remaining[0] && remaining[0].fingerprint) || p.fingerprint;
    profilesUpdated.push({ personKey: p.personKey, sampleCount });
    return { ...p, sampleCount, fingerprint, updatedAt: now };
  });

  writeAll(db);
  return {
    modelId: "lab-local",
    softDelete: true,
    meetingId: mid,
    reason,
    samplesExcluded,
    sampleIds,
    profilesUpdated,
    labFallback: true,
    audit: {
      note:
        "Lab: amostras com excludedAt; filtrar samples onde meetingId e excludedAt."
    }
  };
}
